import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Calendar, Clock, Navigation, MapPin, Loader2, AlertTriangle, X, Play } from "lucide-react";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import api, { formatApiError } from "../lib/api";

// If a driver has two reserved rides whose scheduled pickup times are
// within this many minutes, we assume they'll conflict (typical urban
// ride window). Purely client-side heuristic — driver can still keep both.
const RESERVE_CONFLICT_WINDOW_MIN = 45;

// Teal disc pin matching the request sheet's dropoff pin, but scaled down
// for the mini-map overview of the driver's day queue.
const reservedPin = L.divIcon({
  className: "reserved-pin",
  html: `<div style="width:18px;height:18px;transform:translate(-50%,-50%);"><div style="width:100%;height:100%;border-radius:9999px;background:#00E5FF;box-shadow:0 0 0 3px rgba(0,229,255,0.25),0 3px 8px rgba(0,0,0,0.55);"></div></div>`,
  iconSize: [18, 18], iconAnchor: [9, 9],
});

function FitAll({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) { map.setView(points[0], 13, { animate: true }); return; }
    try {
      map.fitBounds(L.latLngBounds(points), { padding: [30, 30], maxZoom: 14, animate: true });
    } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points.length]);
  return null;
}

/**
 * ReservedRidesQueue
 * ──────────────────
 * Driver-side card that lists all upcoming scheduled rides this driver has
 * reserved, ranked by pickup time. Auto-refreshes every 30s so a fresh
 * reserve from the WS push flow shows up promptly.
 */
export default function ReservedRidesQueue() {
  const { t } = useTranslation();
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [releaseTarget, setReleaseTarget] = useState(null); // orderId whose reason modal is open

  const load = async () => {
    try {
      const r = await api.get("/my/reserved-rides");
      setRides(r.data.reserved_rides || []);
    } catch (_) {}
    finally { setLoading(false); }
  };

  const release = async (orderId, reason) => {
    setBusy(`rel-${orderId}`);
    try {
      await api.post(`/orders/${orderId}/release-reserve`, { reason });
      toast.success(t("dashboard.released", "Reservation released"));
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); setReleaseTarget(null); }
  };

  const startDriving = async (orderId) => {
    setBusy(`start-${orderId}`);
    try {
      const r = await api.post(`/orders/${orderId}/start-driving`);
      toast.success(t("dashboard.startedDriving", "Rider notified — ETA ~{{n}} min", { n: r.data.eta_min }));
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  if (loading) return null;
  if (!rides.length) return null;

  // Mark rides whose scheduled pickup is within CONFLICT_WINDOW of any
  // neighbouring ride so the driver sees at-a-glance which reservations
  // overlap. Uses the pre-sorted order (ascending pickup time).
  const conflicts = new Set();
  for (let i = 0; i < rides.length - 1; i++) {
    const a = rides[i].scheduled_at;
    const b = rides[i + 1].scheduled_at;
    if (!a || !b) continue;
    const diff = Math.abs(new Date(b) - new Date(a)) / 60000;
    if (diff < RESERVE_CONFLICT_WINDOW_MIN) {
      conflicts.add(rides[i].order_id);
      conflicts.add(rides[i + 1].order_id);
    }
  }
  const hasConflicts = conflicts.size > 0;
  const now = Date.now();
  return (
    <div className="mt-4 rounded-2xl border border-electric-teal/30 bg-electric-teal/[0.06] p-4" data-testid="reserved-rides-queue">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-full bg-electric-teal text-obsidian flex items-center justify-center">
          <Calendar size={16} strokeWidth={2.5} />
        </div>
        <div>
          <div className="text-sm font-black text-electric-teal">
            {t("dashboard.reservedQueueTitle", "Your reserved rides ({{n}})", { n: rides.length })}
          </div>
          <div className="text-[10px] text-white/50">
            {t("dashboard.reservedQueueSub", "Ranked by pickup time — no more racing for these")}
          </div>
        </div>
      </div>
      {hasConflicts && (
        <div className="mb-3 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 flex items-start gap-2" data-testid="reserve-conflict-banner">
          <AlertTriangle size={14} className="text-amber-300 shrink-0 mt-0.5" />
          <div>
            <div className="text-[11px] font-black text-amber-300 uppercase tracking-widest">
              {t("dashboard.conflictTitle", "Overlapping reservations")}
            </div>
            <div className="text-[11px] text-white/70 mt-0.5">
              {t("dashboard.conflictBody", "Some rides are booked within {{min}} minutes of each other — drop one before both pickups fire.", { min: RESERVE_CONFLICT_WINDOW_MIN })}
            </div>
          </div>
        </div>
      )}

      {/* Mini-map overview — teal pin for each reserved pickup so the driver
          sees the whole day's route shape at a glance. */}
      {(() => {
        const pts = rides
          .filter((r) => r.pickup_lat && r.pickup_lng)
          .map((r) => [r.pickup_lat, r.pickup_lng]);
        if (pts.length === 0) return null;
        return (
          <div
            className="mb-3 rounded-xl overflow-hidden border border-electric-teal/20"
            style={{ height: 160 }}
            data-testid="reserved-rides-map"
          >
            <MapContainer
              center={pts[0]}
              zoom={12}
              scrollWheelZoom={false}
              zoomControl={false}
              attributionControl={false}
              style={{ width: "100%", height: "100%" }}
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <FitAll points={pts} />
              {rides.filter((r) => r.pickup_lat && r.pickup_lng).map((r, i) => {
                const t = r.scheduled_at
                  ? new Date(r.scheduled_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
                  : "";
                return (
                  <Marker key={r.order_id} position={[r.pickup_lat, r.pickup_lng]} icon={reservedPin}>
                    <Tooltip direction="top" offset={[0, -10]} opacity={1}>
                      #{i + 1} · {t}
                    </Tooltip>
                  </Marker>
                );
              })}
            </MapContainer>
          </div>
        );
      })()}

      <div className="space-y-2">
        {rides.map((o) => {
          const sched = o.scheduled_at ? new Date(o.scheduled_at) : null;
          const minsAway = sched ? Math.max(0, Math.round((sched.getTime() - now) / 60000)) : null;
          return (
            <div
              key={o.order_id}
              className={`rounded-xl bg-obsidian/60 border p-3 flex items-center gap-3 ${
                conflicts.has(o.order_id) ? "border-amber-500/50 ring-1 ring-amber-500/20" : "border-white/10"
              }`}
              data-testid={`reserved-ride-${o.order_id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-widest font-black text-electric-teal">
                    {t(`categories.${o.category}`, o.category)}
                  </span>
                  {sched && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-white/70 font-black tabular-nums">
                      <Clock size={10} className="text-electric-teal" />
                      {sched.toLocaleString(undefined, { weekday: "short", hour: "2-digit", minute: "2-digit" })}
                      {minsAway != null && (
                        <span className="text-white/50 ms-1">· {minsAway < 60 ? `${minsAway}m` : `${Math.round(minsAway / 60)}h`}</span>
                      )}
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-white/70 truncate mt-0.5 inline-flex items-center gap-1">
                  <MapPin size={10} className="text-electric-teal shrink-0" />
                  {o.pickup_address || "—"}
                </div>
                {o.dropoff_address && (
                  <div className="text-[11px] text-white/50 truncate mt-0.5 inline-flex items-center gap-1">
                    <Navigation size={10} className="shrink-0" />
                    {o.dropoff_address}
                  </div>
                )}
              </div>
              <div className="text-end shrink-0 flex flex-col items-end gap-1">
                <div>
                  <div className="text-sm font-black text-electric-teal tabular-nums">
                    {(o.supplier_earnings || (o.total || 0) * 0.9).toFixed(0)}
                  </div>
                  <div className="text-[10px] text-white/50 uppercase">{(o.currency || "USD").toUpperCase()}</div>
                </div>
                <div className="flex gap-1 mt-1">
                  <button
                    type="button"
                    onClick={() => startDriving(o.order_id)}
                    disabled={busy === `start-${o.order_id}`}
                    className="inline-flex items-center gap-1 rounded-full bg-electric-teal hover:bg-electric-teal/90 text-obsidian px-2.5 py-1 text-[10px] font-black transition disabled:opacity-50"
                    data-testid={`start-driving-${o.order_id}`}
                    title={t("dashboard.startDrivingTip", "Notify rider your ETA")}
                  >
                    {busy === `start-${o.order_id}` ? <Loader2 size={9} className="animate-spin" /> : <Play size={9} strokeWidth={3} />}
                    {t("dashboard.startDriving", "Start")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setReleaseTarget(o.order_id)}
                    disabled={busy === `rel-${o.order_id}`}
                    className="inline-flex items-center gap-1 rounded-full border border-white/15 hover:border-red-500/40 hover:bg-red-500/10 text-white/60 hover:text-red-300 px-2.5 py-1 text-[10px] font-black transition disabled:opacity-50"
                    data-testid={`release-reserve-${o.order_id}`}
                    title={t("dashboard.releaseTip", "Free this slot for another driver")}
                  >
                    {busy === `rel-${o.order_id}` ? <Loader2 size={9} className="animate-spin" /> : <X size={9} strokeWidth={3} />}
                    {t("dashboard.release", "Release")}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {releaseTarget && (
        <div
          className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center p-4"
          onClick={() => setReleaseTarget(null)}
          data-testid="release-reason-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm rounded-2xl bg-obsidian border border-white/15 p-5"
          >
            <div className="text-[10px] uppercase tracking-widest font-black text-red-300 mb-1">
              {t("dashboard.releaseWhy", "Why are you releasing?")}
            </div>
            <div className="text-sm font-black text-white mb-4">
              {t("dashboard.releaseWhyBody", "One tap — helps us schedule smarter.")}
            </div>
            <div className="grid gap-2">
              {[
                { key: "overlap", label: t("dashboard.reasonOverlap", "Overlaps with another ride") },
                { key: "personal", label: t("dashboard.reasonPersonal", "Personal / off shift") },
                { key: "too_far", label: t("dashboard.reasonTooFar", "Pickup too far") },
                { key: "other", label: t("dashboard.reasonOther", "Something else") },
              ].map((r) => (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => release(releaseTarget, r.key)}
                  className="rounded-xl bg-white/[0.04] hover:bg-red-500/10 hover:text-red-300 border border-white/10 hover:border-red-500/40 text-white text-sm font-black px-3 py-2.5 text-start transition"
                  data-testid={`release-reason-${r.key}`}
                >
                  {r.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setReleaseTarget(null)}
                className="mt-1 text-xs text-white/50 hover:text-white text-center py-1"
                data-testid="release-reason-cancel"
              >
                {t("dashboard.reasonCancel", "Never mind")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
